import { useState, useMemo } from 'react';
import axios from 'axios';
import _ from 'lodash';

import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import Button from '@mui/material/Button';
import TextField from '@mui/material/TextField';
import IconButton from '@mui/material/IconButton';
import { useTheme, alpha } from '@mui/material/styles';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faXmark } from '@fortawesome/pro-light-svg-icons';

import { aiParseDifferences, latestAiParse } from './aiParseDiff.js';

// Shown in Shipment Details when the user changed what the AI wizard filled in. Saving the change is
// enough: it is recorded as a correction. This lets them add, if they want, why the AI got it wrong.
export default function AiCorrectionNote({ updates, transportationMethod, incoterm, routes, products, opportunityId, org, accesstoken, locked }) {
    const theme = useTheme();
    const aiParse = useMemo(() => latestAiParse(updates), [updates]);
    const differences = useMemo(
        () => (aiParse?.filled ? aiParseDifferences(aiParse.filled, { transportationMethod, incoterm, routes, products }) : []),
        [aiParse, transportationMethod, incoterm, routes, products],
    );
    const [open, setOpen] = useState(false);
    const [why, setWhy] = useState('');
    const [state, setState] = useState(null); // null | 'sending' | 'sent' | { error }
    const [dismissed, setDismissed] = useState(false);

    if (!aiParse || !differences.length || dismissed || locked) return null;

    const changed = _.uniq(differences.map(d => d.what)).slice(0, 4).join(', ');

    async function send() {
        setState('sending');
        try {
            await axios.post(`/api/opportunities/${opportunityId}/ai-correction?accesstoken=${accesstoken}&organizationId=${org.id}`, { why });
            setState('sent');
            setOpen(false);
        } catch (e) {
            setState({ error: e?.response?.data?.detail || e.message });
        }
    }

    return (
        <Box sx={{ mb: '10px', p: '8px 10px', borderRadius: '6px', background: alpha(theme.palette.primary.main, 0.06), border: `1px solid ${alpha(theme.palette.primary.main, 0.2)}` }}>
            <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1 }}>
                <Typography sx={{ fontSize: '12px', flex: 1 }}>
                    {state === 'sent'
                        ? 'Thank you. We use this to read emails better.'
                        : <>You changed what the AI filled in ({changed}). Your changes are saved as they are. {!open && <Button size='small' onClick={() => setOpen(true)} sx={{ textTransform: 'none', fontSize: '12px', p: 0, minWidth: 0, verticalAlign: 'baseline' }}>Tell us what it got wrong</Button>}</>}
                </Typography>
                <IconButton size='small' aria-label='Hide' onClick={() => setDismissed(true)} sx={{ p: '2px' }}><FontAwesomeIcon icon={faXmark} style={{ fontSize: '12px' }} /></IconButton>
            </Box>
            {open && state !== 'sent' && (
                <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <TextField
                        id='ai-correction-why'
                        multiline
                        minRows={2}
                        size='small'
                        autoFocus
                        placeholder='For example: "Rotterdam is the transshipment port, the destination is Felixstowe" or "26 tons is the total, not per container".'
                        value={why}
                        onChange={e => setWhy(e.target.value)}
                        sx={{ background: theme.palette.background.paper, '& textarea': { fontSize: '12px' } }}
                    />
                    {state?.error && <Typography sx={{ fontSize: '12px', color: 'error.main' }}>{state.error}</Typography>}
                    <Box sx={{ display: 'flex', gap: 1, justifyContent: 'flex-end' }}>
                        <Button size='small' onClick={() => setOpen(false)} sx={{ textTransform: 'none' }}>Cancel</Button>
                        <Button size='small' variant='contained' disabled={!why.trim() || state === 'sending'} onClick={send} sx={{ textTransform: 'none' }}>
                            {state === 'sending' ? 'Sending…' : 'Send'}
                        </Button>
                    </Box>
                </Box>
            )}
        </Box>
    );
}
